import React, { useEffect, useState, useRef } from "react";
import {
  Row,
  Col,
  Form,
  Button,
  Container,
  Modal,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import axios from "axios";
import { useSelector } from "react-redux";
import QRCodeStyling from "qr-code-styling";
import logo1 from "./logo1.png";
import { useNavigate } from "react-router-dom";

const ProductForm = () => {
  useEffect(() => {
    window.history.scrollRestoration = "manual"; // Prevent browser from restoring scroll
  }, []);

  const user = useSelector((state) => state.authentication.user);
  const [formData, setFormData] = useState({});
  const [diamonds, setDiamonds] = useState([{}]);
  const [colorStones, setColorStones] = useState([{}]);
  const [golds, setGolds] = useState([{}]);

  const [showModal, setShowModal] = useState(false);
  const [qrTagNo, setQrTagNo] = useState("");
  const qrCodeModalRef = useRef(null);
  const qrModalInstanceRef = useRef(null);
  const [ornaments, setOrnaments] = useState(["All"]);
  const [image, setimage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getResponse = async () => {
    try {
      const res = await axios.get(`/api/products/`);

      const ornamentSet = new Set(
        res.data.map((p) => p.Ornament).filter(Boolean)
      );
      setOrnaments(["All", ...Array.from(ornamentSet).sort()]);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  useEffect(() => {
    getResponse();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...formData, [name]: value };

    const rateWeightMatch = name.match(
      /^(D|CS|G)(\d+)_([CS])?_?(Rate|Weight)$/
    );
    if (rateWeightMatch) {
      const [_, prefix, index] = rateWeightMatch;
      const base = `${prefix}${index}`;
      const weightField = `${base}_Weight`;
      const cRateField = `${base}_C_Rate`;
      const cAmountField = `${base}_C_Amount`;
      const sRateField = `${base}_S_Rate`;
      const sAmountField = `${base}_S_Amount`;

      const weight = parseFloat(
        name === weightField ? value : updatedData[weightField]
      );
      const cRate = parseFloat(
        name === cRateField ? value : updatedData[cRateField]
      );
      const sRate = parseFloat(
        name === sRateField ? value : updatedData[sRateField]
      );

      if (!isNaN(weight) && !isNaN(cRate)) {
        updatedData[cAmountField] = Math.ceil(weight * cRate);
      }
      if (!isNaN(weight) && !isNaN(sRate)) {
        updatedData[sAmountField] = Math.ceil(weight * sRate);
      }

      const g1 = parseFloat(
        name === "G1_Weight" ? value : updatedData["G1_Weight"]
      );
      const g2 = parseFloat(
        name === "G2_Weight" ? value : updatedData["G2_Weight"]
      );
      if (!isNaN(g1) || !isNaN(g2)) {
        const total = (g1 || 0) + (g2 || 0);
        updatedData["S_Making"] = Math.ceil(2000 * total);
      }
    }

    setFormData(updatedData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    let formField = new FormData();
    formField.append("user", user.user_id);

    Object.keys(formData).forEach((key) => {
      formField.append(key, formData[key]);
    });

    if (image !== null) formField.append("image", image);

    try {
      const response = await axios.post("/api/products/", formField);
      // console.log(response.data);
      const submittedTagNo = response.data.Tag_No;
      if (submittedTagNo) {
        setQrTagNo(submittedTagNo);
        setShowModal(true);
      }
    } catch (error) {
      console.error(error.data);
      alert(error);
    }
    setSubmitting(false);
  };

  const renderGroupedFields = (fields, layout = []) => {
    const rows = [];
    let index = 0;
    layout.forEach((colsPerRow) => {
      const rowFields = fields.slice(index, index + colsPerRow);
      rows.push(
        <Row className="mb-3" key={index}>
          {rowFields.map((field) => (
            <Col key={field}>
              <Form.Group controlId={field}>
                <Form.Label>{field.replace(/_/g, " ")}</Form.Label>
                {field === "Ornament" ? (
                  <Form.Select
                    name={field}
                    value={formData[field] || ""}
                    onChange={handleChange}
                  >
                    {ornaments.map((ornament, idx) => (
                      <option key={idx} value={ornament}>
                        {ornament}
                      </option>
                    ))}
                  </Form.Select>
                ) : field.startsWith("Gold_") ? (
                  <Form.Select
                    name={field}
                    value={formData[field] || ""}
                    onChange={handleChange}
                  >
                    <option value="">Select Gold Type</option>
                    <option value="GOLD - 22CT">GOLD - 22CT</option>
                    <option value="GOLD - 18CT">GOLD - 18CT</option>
                  </Form.Select>
                ) : (
                  <Form.Control
                    type="text"
                    name={field}
                    value={formData[field] || ""}
                    onChange={handleChange}
                  />
                )}
              </Form.Group>
            </Col>
          ))}
        </Row>
      );
      index += colsPerRow;
    });
    return rows;
  };

  const renderFields = (label, fields, index = null, type = null) => {
    let layout =
      label.includes("Diamond") || label.includes("Color Stone")
        ? [3, 2, 2]
        : [2, 2, 2];
    return (
      <div className="mb-4" key={index}>
        <h5>{label}</h5>
        {renderGroupedFields(fields, layout)}
        {type && (
          <Button
            variant="danger"
            className="mb-3"
            onClick={() => handleRemove(type, index)}
          >
            Remove
          </Button>
        )}
      </div>
    );
  };

  const handleAdd = (type) => {
    if (type === "diamond" && diamonds.length < 10)
      setDiamonds([...diamonds, {}]);
    if (type === "colorStone" && colorStones.length < 5)
      setColorStones([...colorStones, {}]);
    if (type === "gold" && golds.length < 2) setGolds([...golds, {}]);
  };

  const handleRemove = (type, index) => {
    if (type === "diamond") setDiamonds(diamonds.filter((_, i) => i !== index));
    if (type === "colorStone")
      setColorStones(colorStones.filter((_, i) => i !== index));
    if (type === "gold") setGolds(golds.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (showModal && qrTagNo && qrCodeModalRef.current) {
      const qr = new QRCodeStyling({
        width: 300,
        height: 300,
        data: `https://vjqr.in/#/p/${qrTagNo}`,
        // image: logo1,
        imageOptions: {
          hideBackgroundDots: true,
          imageSize: 0.4,
          margin: 5,
          crossOrigin: "anonymous",
        },
        dotsOptions: {
          type: "extra-rounded",
          color: "#112155",
        },
        backgroundOptions: {
          color: "#ffffff",
        },
        cornersSquareOptions: {
          type: "extra-rounded",
          color: "#152860",
        },
        cornersDotOptions: {
          type: "",
          color: "#152860",
        },
      });
      qrModalInstanceRef.current = qr;
      qrCodeModalRef.current.innerHTML = "";
      qr.append(qrCodeModalRef.current);
    }
  }, [showModal, qrTagNo]);

  return (
    <Container style={{ maxWidth: "80%", padding: "2vh 0" }}>
      <Row>
        <Col md={12}>
          <Form>
            <h2>Product Form</h2>
            {renderFields("General Information", [
              "Tag_No",
              "Ornament",
              "Certificate_No",
            ])}

            <Row className="mb-3">
              <Col>
                <Form.Group controlId="Productimage">
                  <Form.Label>Product image</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => setimage(e.target.files[0] || null)}
                  />
                </Form.Group>
              </Col>
            </Row>

            {diamonds.map((_, i) =>
              renderFields(
                `Diamond ${i + 1}`,
                [
                  `Diamond_0${i + 1}`,
                  `D${i + 1}_Pcs`,
                  `D${i + 1}_Weight`,
                  `D${i + 1}_C_Rate`,
                  `D${i + 1}_C_Amount`,
                  `D${i + 1}_S_Rate`,
                  `D${i + 1}_S_Amount`,
                ],
                i,
                "diamond"
              )
            )}
            <Button
              variant="secondary"
              onClick={() => handleAdd("diamond")}
              className="mb-3"
            >
              Add Diamond
            </Button>

            {colorStones.map((_, i) =>
              renderFields(
                `Color Stone ${i + 1}`,
                [
                  `CStone_0${i + 1}`,
                  `CS${i + 1}_Pcs`,
                  `CS${i + 1}_Weight`,
                  `CS${i + 1}_C_Rate`,
                  `CS${i + 1}_C_Amount`,
                  `CS${i + 1}_S_Rate`,
                  `CS${i + 1}_S_Amount`,
                ],
                i,
                "colorStone"
              )
            )}
            <Button
              variant="secondary"
              onClick={() => handleAdd("colorStone")}
              className="mb-3"
            >
              Add Colour Stone
            </Button>

            {golds.map((_, i) =>
              renderFields(
                `Gold ${i + 1}`,
                [
                  `Gold_0${i + 1}`,
                  `G${i + 1}_Weight`,
                  `G${i + 1}_C_Rate`,
                  `G${i + 1}_C_Amount`,
                  `G${i + 1}_S_Rate`,
                  `G${i + 1}_S_Amount`,
                ],
                i,
                "gold"
              )
            )}
            <Button
              variant="secondary"
              onClick={() => handleAdd("gold")}
              className="mb-3"
            >
              Add Gold
            </Button>

            {renderFields("Making Charges", ["C_Making", "S_Making"])}

            {submitting ? (
              <OverlayTrigger
                overlay={
                  <Tooltip id="tooltip-disabled">
                    Submitting Please Wait
                  </Tooltip>
                }
              >
                <Button disabled variant="primary">
                  Submitting
                </Button>
              </OverlayTrigger>
            ) : (
              <Button variant="primary" onClick={handleSubmit}>
                Submit
              </Button>
            )}
          </Form>
        </Col>
      </Row>

      {/* QR Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>QR Code - {qrTagNo}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <div ref={qrCodeModalRef} />
          <Button
            className="mt-3"
            onClick={() =>
              qrModalInstanceRef.current?.download({
                name: `${qrTagNo}`,
                extension: "jpeg",
              })
            }
          >
            Download QR Code
          </Button>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => navigate(`/p/${qrTagNo}`)}>
            View Product
          </Button>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Add New Product
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProductForm;
